import { i18n } from "../i18n";

export const GRACE_MS = 600_000;
export const BETA_ACCESS_KEY = "classroom-beta-access-v1";
export type BetaDenialReason = "expired" | "revoked" | "update-required" | "sign-in-required" | "denied";
type SavedGrant = {
  userId: string;
  remainingMs: number;
  seenWall: number;
  denied: boolean;
  denialReason?: BetaDenialReason;
  revision?: string;
};

export function betaResponseDenialReason(status: number, body: unknown): BetaDenialReason {
  if (status === 401) return "sign-in-required";
  if (
    body &&
    typeof body === "object" &&
    "reason" in body &&
    (body.reason === "expired" || body.reason === "revoked" || body.reason === "update-required")
  )
    return body.reason;
  return "denied";
}

export function betaDenialMessage(reason: BetaDenialReason) {
  return {
    expired: i18n.t("accessExpired", { ns: "auth" }),
    revoked: i18n.t("accessRevoked", { ns: "auth" }),
    "update-required": i18n.t("accessUpdateRequired", { ns: "auth" }),
    "sign-in-required": "登入已失效；請重新登入。",
    denied: i18n.t("accessDenied", { ns: "auth" }),
  }[reason];
}

function readGrant(saved: string | null): SavedGrant | null {
  try {
    const value = JSON.parse(saved || "null");
    if (
      value &&
      typeof value.userId === "string" &&
      Number.isFinite(value.remainingMs) &&
      value.remainingMs >= 0 &&
      value.remainingMs <= GRACE_MS &&
      Number.isFinite(value.seenWall) &&
      typeof value.denied === "boolean"
    ) {
      const reason = value.denialReason;
      return {
        userId: value.userId,
        remainingMs: value.remainingMs,
        seenWall: value.seenWall,
        denied: value.denied,
        revision: typeof value.revision === "string" ? value.revision : undefined,
        denialReason:
          reason === "expired" || reason === "revoked" || reason === "update-required" || reason === "sign-in-required"
            ? reason
            : "denied",
      };
    }
  } catch {
    /* Missing or damaged cache never grants write access. */
  }
  return null;
}

// This is a normal-use gate, not protection against someone editing browser storage.
export class BetaAccessClock {
  private grantState: SavedGrant | null = null;
  private lastMono: number;
  constructor(
    saved: string | null,
    private now = () => ({ wall: Date.now(), mono: performance.now() }),
    private persist: (value: string) => void = () => undefined,
    private readSaved?: () => string | null,
  ) {
    this.lastMono = now().mono;
    this.grantState = readGrant(saved);
    this.remaining();
  }
  userId() {
    return this.grantState?.userId;
  }
  savedUserId() {
    try {
      return (this.readSaved ? readGrant(this.readSaved()) : this.grantState)?.userId;
    } catch {
      return undefined;
    }
  }
  denied() {
    return this.grantState?.denied ?? false;
  }
  denialReason(): BetaDenialReason | null {
    return this.grantState?.denied ? this.grantState.denialReason || "denied" : null;
  }
  revision() {
    try {
      return (this.readSaved ? readGrant(this.readSaved()) : this.grantState)?.revision || null;
    } catch {
      return null;
    }
  }
  grant(userId: string, duration: number, expectedRevision?: string | null) {
    if (expectedRevision !== undefined && this.revision() !== expectedRevision) {
      this.remaining();
      return false;
    }
    const { wall, mono } = this.now();
    this.lastMono = mono;
    this.grantState = {
      userId,
      remainingMs: Number.isFinite(duration) ? Math.max(0, Math.min(GRACE_MS, duration)) : 0,
      seenWall: wall,
      denied: false,
      revision: crypto.randomUUID(),
    };
    this.save();
    return true;
  }
  deny(reason: BetaDenialReason = "denied", userId = this.grantState?.userId) {
    if (!userId) return;
    this.grantState = {
      userId,
      remainingMs: 0,
      seenWall: this.now().wall,
      denied: true,
      denialReason: reason,
      revision: crypto.randomUUID(),
    };
    this.save();
  }
  remaining() {
    if (!this.grantState) return 0;
    const { wall, mono } = this.now();
    let shared: SavedGrant | null = null;
    // Recheck shared storage before every write guard so another tab cannot revive a denial.
    // A revision changes only on a server decision; timer ticks cannot replace a newer decision.
    if (this.readSaved) {
      try {
        shared = readGrant(this.readSaved());
      } catch {
        /* Inaccessible storage must not preserve write access. */
      }
      if (!shared || shared.userId !== this.grantState.userId) {
        this.grantState.denied = true;
        this.grantState.remainingMs = 0;
        this.grantState.denialReason = "sign-in-required";
        return 0;
      }
      if (shared.revision !== this.grantState.revision || shared.denied) {
        this.grantState = { ...shared };
        this.lastMono = mono;
      }
    }
    if (this.grantState.denied) return 0;
    const wallElapsed = wall - this.grantState.seenWall;
    const monoElapsed = mono - this.lastMono;
    // Never let clock rollback, sleep, or reloading start another ten minutes.
    this.grantState.remainingMs =
      wallElapsed < 0 || monoElapsed < 0
        ? 0
        : Math.max(0, this.grantState.remainingMs - Math.max(wallElapsed, monoElapsed));
    if (shared) {
      const sharedElapsed = wall - shared.seenWall;
      this.grantState.remainingMs = Math.min(
        this.grantState.remainingMs,
        sharedElapsed < 0 ? 0 : Math.max(0, shared.remainingMs - sharedElapsed),
      );
    }
    this.grantState.seenWall = Math.max(wall, this.grantState.seenWall);
    this.lastMono = mono;
    if (this.readSaved && this.revision() !== (this.grantState.revision || null)) return 0;
    this.save();
    return this.grantState.denied ? 0 : this.grantState.remainingMs;
  }
  canWrite() {
    return this.remaining() > 0;
  }
  private save() {
    if (this.grantState) {
      try {
        this.persist(JSON.stringify(this.grantState));
      } catch {
        this.grantState.remainingMs = 0;
      }
    }
  }
}
